/**
 * GridsScreen — admin editor for leverage grids (AS-IS LTV / LTC / LTARV / TLTC
 * per borrower class), grouped by product + purpose, per program (RTL / GUC).
 *
 * Ported from digital_sizer/pages/AdminGrids.tsx into eastviewos: same
 * /admin/leverage-grids read/write + audit-log history, but recreated in MUI to
 * the eastviewos aesthetic (master list left, editor right) and with react-query
 * swapped for plain useState/useEffect via adminPricingApi. The program is an
 * in-page toggle rather than a route param.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import GridOnOutlined from "@mui/icons-material/GridOnOutlined";
import HistoryRounded from "@mui/icons-material/HistoryRounded";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import ChangeHistoryDrawer from "../../components/admin/ChangeHistoryDrawer";
import { adminPricingApi } from "../../api/adminPricingApi";
import ProgramToggle from "./ProgramToggle";

const PROGRAMS = ["RTL", "GUC"];
const LEVERAGE_FIELDS = [
  { key: "as_is_ltv", label: "AS-IS LTV" },
  { key: "ltc", label: "LTC" },
  { key: "ltarv", label: "LTARV" },
  { key: "tltc", label: "TLTC" },
];

const groupKey = (r) => `${r.loan_product_type}::${r.loan_purpose}`;

export default function GridsScreen() {
  const [program, setProgram] = useState("RTL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [commitMsg, setCommitMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback((prog) => {
    setLoading(true);
    return adminPricingApi
      .getLeverageGrids(prog)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setSelectedKey(null);
    load(program);
  }, [program, load]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of rows || []) {
      const key = groupKey(r);
      if (!m.has(key))
        m.set(key, { key, product: r.loan_product_type, purpose: r.loan_purpose, rows: [] });
      m.get(key).rows.push(r);
    }
    return [...m.values()];
  }, [rows]);

  // Auto-select the first group once data lands.
  useEffect(() => {
    if (groups.length === 0 || selectedKey != null) return;
    setSelectedKey(groups[0].key);
  }, [groups, selectedKey]);

  // Hydrate the editable draft from the selected group.
  useEffect(() => {
    if (selectedKey == null) {
      setDraft(null);
      return;
    }
    const g = groups.find((x) => x.key === selectedKey);
    setDraft(g ? g.rows.map((r) => ({ ...r })) : null);
  }, [selectedKey, groups]);

  const onSave = useCallback(() => {
    if (!draft) return;
    setSaving(true);
    const others = (rows || []).filter((r) => groupKey(r) !== selectedKey);
    // eslint-disable-next-line no-unused-vars
    const all = [...others, ...draft].map(({ id, _id, ...rest }) => rest);
    adminPricingApi
      .saveLeverageGrids(program, all, commitMsg)
      .then(() => {
        setCommitMsg("");
        return load(program);
      })
      .catch((e) =>
        window.alert(`Save failed: ${e?.response?.data?.detail || e?.message || e}`),
      )
      .finally(() => setSaving(false));
  }, [draft, rows, selectedKey, program, commitMsg, load]);

  const setCell = (rowIdx, key, raw) => {
    const v = raw === "" ? null : Number(raw);
    setDraft((prev) =>
      prev ? prev.map((row, i) => (i === rowIdx ? { ...row, [key]: v } : row)) : prev,
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "360px minmax(0, 1fr)" },
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Master list */}
      <Flx
        column
        sx={{
          height: "100%",
          minHeight: 0,
          borderRight: "1px solid #e5e7eb",
          bgcolor: "#fcfcfd",
        }}
      >
        <Box sx={{ px: 1.75, pt: 2, pb: 1.5, borderBottom: "1px solid #d8dee4" }}>
          <Flx ac g={1} sx={{ mb: 1.25 }}>
            <GridOnOutlined sx={{ fontSize: 18, color: "#1F5AF2" }} />
            <Txt sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              Leverage Grids
            </Txt>
          </Flx>
          <ProgramToggle value={program} options={PROGRAMS} onChange={setProgram} />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.25, py: 1 }}>
          {loading ? (
            <Flx center sx={{ py: 5 }}>
              <CircularProgress size={20} sx={{ color: "#1F5AF2" }} />
            </Flx>
          ) : groups.length === 0 ? (
            <Flx column center sx={{ py: 5, px: 2, textAlign: "center", color: "#94a3b8" }}>
              <GridOnOutlined sx={{ fontSize: 30, mb: 1 }} />
              <Txt sx={{ fontSize: 12.5 }}>
                No grids loaded for {program}. Leverage grids are seeded from Excel
                sources.
              </Txt>
            </Flx>
          ) : (
            groups.map((g) => {
              const sel = g.key === selectedKey;
              return (
                <Box
                  key={g.key}
                  component="button"
                  type="button"
                  onClick={() => setSelectedKey(g.key)}
                  sx={{
                    appearance: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    width: "100%",
                    border: "1px solid",
                    borderColor: sel ? "#1F5AF2" : "transparent",
                    bgcolor: sel ? "#f1f6f9" : "transparent",
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 0.85,
                    mb: 0.25,
                    "&:hover": { bgcolor: sel ? "#e4eef3" : "#e8eef2" },
                  }}
                >
                  <Flx ac jb g={1}>
                    <Txt sx={{ fontSize: 13, fontWeight: sel ? 700 : 600, color: "#0f172a" }}>
                      {g.product}
                    </Txt>
                    <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>
                      {g.rows.length} rows
                    </Txt>
                  </Flx>
                  <Txt sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25 }}>{g.purpose}</Txt>
                </Box>
              );
            })
          )}
        </Box>
      </Flx>

      {/* Editor */}
      <Box sx={{ minHeight: 0, overflowY: "auto", p: { xs: 2, md: 3 } }}>
        {!draft ? (
          <Flx column center sx={{ height: "100%", color: "#94a3b8", textAlign: "center", px: 3 }}>
            <GridOnOutlined sx={{ fontSize: 40 }} />
            <Txt sx={{ fontSize: 16, fontWeight: 600, color: "#334155", mt: 1.5 }}>
              Select a grid
            </Txt>
            <Txt sx={{ fontSize: 13, mt: 0.5, maxWidth: 360 }}>
              Choose a product / purpose on the left to edit leverage values.
            </Txt>
          </Flx>
        ) : (
          <Box sx={{ maxWidth: 760 }}>
            <Flx ac jb g={1} sx={{ mb: 0.5 }}>
              <Txt sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                {draft[0]?.loan_product_type}
              </Txt>
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryRounded sx={{ fontSize: 16 }} />}
                onClick={() => setHistoryOpen(true)}
                sx={{ textTransform: "none" }}
              >
                History
              </Button>
            </Flx>
            <Txt sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
              {draft[0]?.loan_purpose} · {program}
            </Txt>

            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                overflow: "hidden",
                mb: 1.5,
              }}
            >
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box
                  component="thead"
                  sx={{
                    "& th": {
                      bgcolor: "#f1f6f9",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      py: 1,
                      px: 1.5,
                      borderBottom: "1px solid #e5e7eb",
                    },
                    "& th:not(:first-of-type)": { textAlign: "right" },
                  }}
                >
                  <Box component="tr">
                    <Box component="th">Class</Box>
                    {LEVERAGE_FIELDS.map((f) => (
                      <Box component="th" key={f.key}>
                        {f.label}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box
                  component="tbody"
                  sx={{
                    "& td": {
                      py: 0.75,
                      px: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                      verticalAlign: "middle",
                    },
                    "& tr:last-of-type td": { borderBottom: "none" },
                    "& tr:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  {draft.map((r, i) => (
                    <Box component="tr" key={i}>
                      <Box component="td" sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                        {r.borrower_class}
                      </Box>
                      {LEVERAGE_FIELDS.map((f) => (
                        <Box component="td" key={f.key} sx={{ textAlign: "right" }}>
                          <TextField
                            type="number"
                            size="small"
                            value={r[f.key] == null ? "" : r[f.key]}
                            onChange={(e) => setCell(i, f.key, e.target.value)}
                            inputProps={{ step: 0.01, style: { textAlign: "right" } }}
                            sx={{
                              width: 92,
                              "& input": {
                                py: 0.5,
                                fontSize: 13,
                                fontVariantNumeric: "tabular-nums",
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Txt sx={{ fontSize: 11.5, color: "#94a3b8", mb: 3.5 }}>
              Values are decimals (e.g. 0.85 = 85%). An empty cell saves as null.
            </Txt>

            <TextField
              size="small"
              fullWidth
              label="Commit message (optional)"
              placeholder="Why are you saving this change?"
              value={commitMsg}
              onChange={(e) => setCommitMsg(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1.5 }}
            />

            <Button
              variant="contained"
              disableElevation
              onClick={onSave}
              disabled={saving}
              sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}
            >
              {saving ? "Saving…" : "Save Grid"}
            </Button>
          </Box>
        )}
      </Box>

      <ChangeHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        entityType="leverage_grid"
        entityId={program}
      />
    </Box>
  );
}
